import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Upload, X, Image as ImageIcon, FileText, ArrowLeft } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import api from '../services/api';

const ChildSubmitWork = () => {
  const [child, setChild] = useState(null);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    subjectId: '',
    title: '',
    description: ''
  });

  useEffect(() => {
    const childUser = localStorage.getItem('childUser');
    const childToken = localStorage.getItem('childToken');
    
    if (!childUser || !childToken) {
      navigate('/child/login');
      return;
    }
    
    // Set API token from childToken for authenticated requests
    api.setToken(childToken);
    
    const childData = JSON.parse(childUser);
    setChild(childData);
    
    // Load photo from localStorage immediately for instant display
    const cachedPhotoUrl = localStorage.getItem(`child_photo_${childData.id}`);
    if (cachedPhotoUrl && cachedPhotoUrl !== 'null') {
      setProfilePhoto(cachedPhotoUrl);
    }
    
    loadSubjects();
  }, [navigate]);

  const loadSubjects = async () => {
    try {
      const subjectsData = await api.getSubjects();
      setSubjects(subjectsData);
    } catch (error) {
      console.error('Failed to load subjects:', error);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.subjectId || !formData.title) {
      toast({
        title: 'Missing Information',
        description: 'Please fill in subject and title',
        variant: 'destructive'
      });
      return;
    }

    setSubmitting(true);

    try {
      // Upload files to object storage if any
      const attachments = [];
      for (const file of selectedFiles) {
        try {
          const uploadResult = await api.uploadFile(file);
          attachments.push({
            type: file.type?.startsWith('image/') ? 'image' : 'document',
            name: file.name,
            url: uploadResult.url
          });
        } catch (error) {
          console.error('File upload failed:', error);
        }
      }

      // Submit the work
      await api.submitBonusWork({
        child_id: child.id,
        subject_id: formData.subjectId,
        title: formData.title,
        description: formData.description,
        attachments: attachments
      });

      toast({
        title: 'Work Submitted!',
        description: 'Your work has been sent to your parent for review. Great job!'
      });

      // Reset form
      setFormData({ subjectId: '', title: '', description: '' });
      setSelectedFiles([]);
      
      // Navigate back to dashboard
      setTimeout(() => navigate('/child/dashboard'), 1500);
    } catch (error) {
      console.error('Submit failed:', error);
      toast({
        title: 'Submit Failed',
        description: error.message || 'Failed to submit work',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!child) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6 shadow-lg">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="ghost"
              className="text-white hover:bg-white/20"
              onClick={() => navigate('/child/dashboard')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            {/* Profile Photo */}
            <div
              className="w-12 h-12 rounded-full bg-white flex items-center justify-center text-xl font-bold shadow-lg overflow-hidden cursor-pointer"
              style={{ color: child.profileColor }}
              onClick={() => navigate('/child/dashboard')}
            >
              {profilePhoto ? (
                <img src={profilePhoto} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                child.name.charAt(0)
              )}
            </div>
          </div>
          <h1 className="text-3xl font-bold">Submit Your Work</h1>
          <p className="text-purple-100">Show what you've learned!</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        <Card className="p-8 border-0 shadow-xl bg-white">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="subject" className="text-lg">Subject</Label>
              <Select
                value={formData.subjectId}
                onValueChange={(value) => setFormData({ ...formData, subjectId: value })}
              >
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Choose a subject" />
                </SelectTrigger>
                <SelectContent>
                  {subjects.map(subject => (
                    <SelectItem key={subject.id} value={subject.id.toString()}>
                      <div className="flex items-center gap-2">
                        <span>{subject.icon}</span>
                        <span>{subject.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="title" className="text-lg">What did you work on?</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Math worksheet, Reading log, Science project..."
                className="mt-2 text-lg"
                required
              />
            </div>

            <div>
              <Label htmlFor="description" className="text-lg">Tell us about it!</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="I completed all the problems... I learned about..."
                rows={4}
                className="mt-2 text-lg"
                required
              />
            </div>

            {/* File Upload */}
            <div>
              <Label className="text-lg">Upload Photos or Documents</Label>
              <p className="text-sm text-slate-600 mb-2">💡 Tip: You can select multiple photos at once! (Hold Ctrl/Cmd or Shift to select multiple)</p>
              <div className="mt-2 grid grid-cols-2 gap-4">
                {/* Take Photo Button */}
                <div className="border-2 border-dashed border-purple-300 rounded-xl p-6 text-center hover:border-purple-400 transition-colors cursor-pointer bg-purple-50/50">
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                    id="camera-upload"
                  />
                  <label htmlFor="camera-upload" className="cursor-pointer">
                    <div className="w-16 h-16 bg-purple-200 rounded-full flex items-center justify-center mx-auto mb-3">
                      <svg className="w-10 h-10 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <p className="text-purple-700 font-semibold">Take Photos</p>
                    <p className="text-purple-500 text-xs mt-1">Add multiple photos</p>
                  </label>
                </div>

                {/* Choose from Gallery Button */}
                <div className="border-2 border-dashed border-purple-300 rounded-xl p-6 text-center hover:border-purple-400 transition-colors cursor-pointer bg-purple-50/50">
                  <input
                    type="file"
                    multiple
                    accept="image/*,.pdf,.doc,.docx"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="file-upload"
                  />
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <div className="w-16 h-16 bg-pink-200 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Upload className="w-10 h-10 text-pink-600" />
                    </div>
                    <p className="text-purple-700 font-semibold">Choose Multiple Files</p>
                    <p className="text-purple-500 text-xs mt-1">Select as many as you need!</p>
                  </label>
                </div>
              </div>

              {selectedFiles.length > 0 && (
                <div className="mt-4 space-y-2">
                  <Label className="text-base">Selected Files:</Label>
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200">
                      <div className="flex items-center gap-3">
                        {file.type.startsWith('image/') ? (
                          <ImageIcon className="w-5 h-5 text-purple-600" />
                        ) : (
                          <FileText className="w-5 h-5 text-purple-600" />
                        )}
                        <span className="text-slate-700 font-medium">{file.name}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="text-red-600 hover:text-red-700 p-1"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-4 pt-4">
              <Button
                type="submit"
                className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-6 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!formData.subjectId || !formData.title || submitting}
              >
                {submitting ? 'Submitting...' : 'Submit Work'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/child/dashboard')}
                className="px-8 py-6"
              >
                Cancel
              </Button>
            </div>
          </form>
        </Card>

        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-blue-900 text-center">
            🌟 After your parent reviews your work, you'll earn tokens to spend in the Rewards Store!
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChildSubmitWork;